<script lang="ts">
  // Études « Les importations brésiliennes de vin » (/brasil/) et « Les importations américaines de vin »
  // (/estados-unidos/) — même gabarit que l'étude France /francia/ (sommaire, sections numérotées, une phrase-réponse
  // sous chaque titre ; 22/10/2026, Bruno : « peux-tu faire l'étude macro Brésil et USA sur le même format que
  // France ? »). Une seule app pour les deux pays (prop pays) ; sélecteurs : année et pays d'origine.
  import { onMount } from 'svelte';
  import { t, tf, lang } from './lib/i18n.svelte';
  import { fetchMeta, fetchBundle, fetchWorld, type Meta, type Bundle, type World, type MonthRow, type CodeRow } from './lib/api';
  import { etudeToc, numberOf, flatIds, regId } from './lib/toc';
  import { usd, mhl, usdL, pct, sg, nf, dUsd, chg, cls, monthName } from './lib/format';
  import { CATS, catOf, ORGANIC, ZONES, zoneOf, regionName, portName, type Pays } from './lib/pays';
  import * as X from './lib/texts';
  import Toc from './blocks/Toc.svelte';
  import SecTitle from './blocks/SecTitle.svelte';
  import LineChart from './blocks/LineChart.svelte';
  import MarketPicker from './blocks/MarketPicker.svelte';
  import BrMap from './blocks/BrMap.svelte';
  import UsMap from './blocks/UsMap.svelte';

  let { pays: P }: { pays: Pays } = $props();

  interface Loaded { year: number; o: string; mm: number; partial: boolean; prevY: number | null; wYear: number; B: Bundle; W: World | null }

  let meta = $state<Meta | null>(null);
  let year = $state(0);
  let origin = $state('');
  let D = $state<Loaded | null>(null);
  let booting = $state(true);
  let busy = $state(false);
  let err = $state('');
  let showAllOrig = $state(false);
  let showPorts = $state(false);

  // ── Noms de pays dans la langue de l'utilisateur (Intl), iso3 → iso2 depuis la base ──
  const iso2Of = $derived(new Map<string, string>([
    ...(meta?.origins || []).filter((c) => c.iso2).map((c) => [c.iso3, c.iso2 as string] as [string, string]),
    ...(D?.W?.names || []).filter((c) => c.iso2).map((c) => [c.iso3, c.iso2 as string] as [string, string]),
  ]));
  const fallbackOf = $derived(new Map<string, string>((meta?.origins || []).map((c) => [c.iso3, (lang() === 'fr' ? c.country_fr : lang() === 'en' ? c.country_en : c.country_es) || c.raw || c.iso3])));
  const FIX: Record<string, Record<string, string>> = { HK: { es: 'Hong Kong', fr: 'Hong Kong', en: 'Hong Kong' }, MO: { es: 'Macao', fr: 'Macao', en: 'Macao' } };
  const dn = $derived.by(() => { try { return new Intl.DisplayNames([lang()], { type: 'region' }); } catch { return null; } });
  function cname(iso3: string): string {
    const i2 = iso2Of.get(iso3);
    if (i2 && FIX[i2]) return FIX[i2][lang()];
    try { if (i2 && dn) { const s = dn.of(i2); if (s && s !== i2) return s; } } catch { /* code inconnu */ }
    return fallbackOf.get(iso3) || iso3;
  }
  const catShort = (key: string) => t('cat.short.' + key.slice(4));
  const viaName = (v: string) => { const s = t('via.' + v); return s === 'via.' + v ? (v ? v.charAt(0) + v.slice(1).toLowerCase() : t('via.other')) : s; };

  // ── Démarrage : méta, paramètres d'URL (?ano=2025&o=ARG) ──
  async function boot() {
    booting = true; err = '';
    try {
      const m = await fetchMeta(P);
      meta = m;
      const p = new URLSearchParams(location.search);
      const wantY = parseInt(p.get('ano') || '', 10), wantO = (p.get('o') || '').toUpperCase();
      year = m.years.some((y) => y.ano === wantY) ? wantY : m.latest_complete_year;
      origin = m.origins.some((x) => x.iso3 === wantO) ? wantO : '';
    } catch (e) { err = e instanceof Error ? e.message : 'Error'; }
    finally { booting = false; }
  }
  onMount(boot);

  let reqId = 0;
  async function load(y: number, o: string) {
    if (!meta) return;
    const my = ++reqId;
    busy = true; err = '';
    try {
      const info = meta.years.find((r) => r.ano === y)!;
      const wYear = Math.min(y, meta.latest_complete_year);
      const [B, W] = await Promise.all([fetchBundle(P, y, o || undefined), fetchWorld(P, wYear).catch(() => null)]);
      if (my !== reqId) return;
      D = { year: y, o, mm: info.max_mes, partial: info.max_mes < 12, prevY: y - 1 >= meta.min_year ? y - 1 : null, wYear: W?.ano || wYear, B, W };
    } catch (e) {
      if (my === reqId) err = e instanceof Error ? e.message : 'Error';
    } finally {
      if (my === reqId) { busy = false; scheduleHashScroll(); }
    }
  }
  $effect(() => {
    const y = year, o = origin;
    if (booting || !y) return;
    showAllOrig = false;
    try {
      const u = new URL(location.href);
      if (meta && y !== meta.latest_complete_year) u.searchParams.set('ano', String(y)); else u.searchParams.delete('ano');
      if (o) u.searchParams.set('o', o); else u.searchParams.delete('o');
      history.replaceState(null, '', u);
    } catch { /* noop */ }
    load(y, o);
  });

  // ── Sommaire : numérotation, scroll-spy, liens profonds ──
  const RID = $derived(regId(P));
  const toc = $derived(etudeToc(P, !!origin));
  const n = (id: string) => numberOf(toc, id);
  let activeSec = $state('');
  function goSection(id: string) {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    try { const u = new URL(location.href); u.hash = id; history.replaceState(null, '', u); } catch { /* noop */ }
    activeSec = id;
  }
  let pendingHash = true;
  function scheduleHashScroll() {
    if (!pendingHash) return;
    const h = (location.hash || '').slice(1);
    if (!h) { pendingHash = false; return; }
    setTimeout(() => { const el = document.getElementById(h); if (el) { el.scrollIntoView({ block: 'start' }); activeSec = h; pendingHash = false; } }, 150);
  }
  onMount(() => {
    let raf = 0;
    const onScroll = () => {
      const ids = flatIds(toc);
      let cur = ids[0];
      for (const id of ids) { const el = document.getElementById(id); if (!el) continue; if (el.getBoundingClientRect().top <= 140) cur = id; else break; }
      activeSec = cur;
    };
    const h = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; onScroll(); }); };
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  });

  // ── Calculs ──
  const COLS = ['#B95955', '#58212D', '#3C4E5A', '#B07340', '#8FA0AB'];
  const sumY = (rows: MonthRow[], y: number, mmax = 12) => rows.filter((r) => r.ano === y && r.mes <= mmax).reduce((a, r) => ({ v: a.v + r.v, l: a.l + r.l }), { v: 0, l: 0 });
  function catAgg(rows: CodeRow[], y: number, mmax: number) {
    const m = new Map<string, { v: number; l: number }>();
    for (const r of rows) {
      if (r.ano !== y || r.mes > mmax) continue;
      const k = catOf(P, r.code, r.hs6), a = m.get(k) || { v: 0, l: 0 };
      a.v += r.v; a.l += r.l; m.set(k, a);
    }
    return m;
  }
  const mktOpts = $derived((meta?.origins || []).map((c) => ({ iso: c.iso3, name: cname(c.iso3), flag: c.flag_url })).sort((a, b) => a.name.localeCompare(b.name, lang())));
  const originName = $derived(origin ? cname(origin) : '');
  const partialInfo = $derived(meta?.years.find((y) => y.max_mes < 12) || null);
  const lastInfo = $derived(meta ? meta.years[meta.years.length - 1] : null);
  const yearLabel = (y: number) => { const i = meta?.years.find((r) => r.ano === y); return i && i.max_mes < 12 ? tf('ctl.partial', { y, m: monthName(i.max_mes, true) }) : String(y); };
  const src = $derived(t('src'));

  const M = $derived.by(() => {
    if (!D || !meta) return null;
    const { year: y, mm, partial, prevY, B } = D;
    const cur = sumY(B.monthly, y), prev = prevY ? sumY(B.monthly, prevY, mm) : null;
    const p = cur.l ? cur.v / cur.l : 0, pp = prev && prev.l ? prev.v / prev.l : null;
    const completeYears = meta.years.filter((r) => r.max_mes === 12).map((r) => r.ano);
    const yearsTbl = meta.years.map((r) => {
      const s = sumY(B.monthly, r.ano), part = r.max_mes < 12;
      const ps = r.ano - 1 >= meta!.min_year ? sumY(B.monthly, r.ano - 1, r.max_mes) : null;
      return { ano: r.ano, part, v: s.v, l: s.l, d: ps && ps.v ? chg(s.v, ps.v) : null };
    });
    // Saisonnalité : année affichée (ou dernière complète), moyenne des autres années complètes, année en cours
    const sY = partial ? meta.latest_complete_year : y;
    const others = completeYears.filter((a) => a !== sY);
    const mv = (a: number, m: number) => B.monthly.find((r) => r.ano === a && r.mes === m)?.v ?? null;
    const sCur = Array.from({ length: 12 }, (_, i) => mv(sY, i + 1));
    const sAvg = Array.from({ length: 12 }, (_, i) => { const xs = others.map((a) => mv(a, i + 1) ?? 0); return xs.length ? xs.reduce((s, v) => s + v, 0) / xs.length : null; });
    const sPart = partialInfo && partialInfo.ano !== sY ? Array.from({ length: 12 }, (_, i) => mv(partialInfo.ano, i + 1)) : null;
    const curVals = sCur.map((v) => v ?? 0);
    const hiM = curVals.indexOf(Math.max(...curVals)) + 1, loM = curVals.indexOf(Math.min(...curVals.filter((v) => v > 0))) + 1;

    // Catégories de vin (codes douaniers regroupés)
    const cc = catAgg(B.codes, y, mm), cp = prevY ? catAgg(B.codes, prevY, mm) : null;
    const cTot = [...cc.values()].reduce((a, r) => ({ v: a.v + r.v, l: a.l + r.l }), { v: 0, l: 0 });
    const cats = CATS[P].map((c) => {
      const a = cc.get(c.key) || { v: 0, l: 0 };
      return { key: c.key, n: t(c.key), short: catShort(c.key), v: a.v, l: a.l, sh: cTot.v ? (a.v / cTot.v) * 100 : 0, shL: cTot.l ? (a.l / cTot.l) * 100 : 0, d: cp ? chg(a.v, cp.get(c.key)?.v) : null };
    }).filter((c) => c.v > 0).sort((a, b) => b.v - a.v);
    const organic = P === 'usa' && cTot.v ? (B.codes.filter((r) => r.ano === y && r.mes <= mm && ORGANIC.has(r.code)).reduce((s, r) => s + r.v, 0) / cTot.v) * 100 : null;
    // Évolution des catégories (années complètes) : un graphique pour les grandes, un pour les autres
    const evoYears = completeYears.filter((a) => a >= meta!.min_year);
    const annual = new Map<string, number>();
    for (const r of B.codes) { const k = r.ano + '|' + catOf(P, r.code, r.hs6); annual.set(k, (annual.get(k) || 0) + r.v); }
    const order = [...new Set([...cats.map((c) => c.key), ...CATS[P].map((c) => c.key)])];
    const catSeries = order.map((k) => ({ key: k, name: catShort(k), values: evoYears.map((a) => (annual.get(a + '|' + k) || 0) / 1e6) })).filter((s) => s.values.some((v) => v > 0.05));
    const lastV = (s: { values: number[] }) => s.values[s.values.length - 1] || 0;
    const topV = Math.max(0, ...catSeries.map(lastV));
    const evoBig = catSeries.filter((s) => lastV(s) >= 0.2 * topV).map((s, i) => ({ ...s, color: COLS[i % 5], dots: true }));
    const evoSmall = catSeries.filter((s) => lastV(s) < 0.2 * topV).map((s, i) => ({ ...s, color: COLS[i % 5], dots: true }));

    // Pays d'origine (classement sur tout le marché)
    const opv = new Map(B.origins.prev.map((r) => [r.iso3, r]));
    const oTot = B.origins.cur.reduce((s, r) => s + r.v, 0);
    const rows = B.origins.cur.filter((r) => r.v > 0).sort((a, b) => b.v - a.v).map((r, i) => {
      const pv = opv.get(r.iso3)?.v;
      return { iso3: r.iso3, n: cname(r.iso3), v: r.v, l: r.l, rank: i + 1, sh: oTot ? (r.v / oTot) * 100 : 0, y: chg(r.v, pv), price: r.l ? r.v / r.l : null, dv: prevY ? r.v - (pv || 0) : null };
    });
    const mine = origin ? rows.find((r) => r.iso3 === origin) || null : null;
    const gone = prevY ? B.origins.prev.filter((r) => r.v > 0 && !B.origins.cur.some((c) => c.iso3 === r.iso3 && c.v > 0)).map((r) => ({ iso3: r.iso3, n: cname(r.iso3), v: 0, l: 0, rank: 0, sh: 0, y: -100, price: null, dv: -r.v })) : [];
    const withDv = [...rows, ...gone].filter((r) => r.dv != null);
    const movers = { up: [...withDv].filter((r) => (r.dv as number) > 0).sort((a, b) => (b.dv as number) - (a.dv as number)).slice(0, 5), dn: [...withDv].filter((r) => (r.dv as number) < 0).sort((a, b) => (a.dv as number) - (b.dv as number)).slice(0, 5) };
    // Grandes zones : année et mêmes mois de l'an passé, puis part de chaque zone par année complète
    const zSum = (rs: { iso3: string; v: number }[]) => { const m = new Map<string, number>(); for (const r of rs) m.set(zoneOf(r.iso3), (m.get(zoneOf(r.iso3)) || 0) + r.v); return m; };
    const zc = zSum(B.origins.cur), zp = zSum(B.origins.prev);
    const zones = ZONES.map((k) => ({ k, n: t('zone.' + k), v: zc.get(k) || 0, sh: oTot ? ((zc.get(k) || 0) / oTot) * 100 : 0, d: prevY ? chg(zc.get(k), zp.get(k)) : null,
      top: rows.filter((r) => zoneOf(r.iso3) === k).slice(0, 4).map((r) => r.n) })).filter((z) => z.v > 0);
    const zYears = completeYears;
    const zTs = new Map<string, number>(), zTot = new Map<number, number>();
    for (const r of B.originsTs) { zTs.set(r.ano + '|' + zoneOf(r.iso3), (zTs.get(r.ano + '|' + zoneOf(r.iso3)) || 0) + r.v); zTot.set(r.ano, (zTot.get(r.ano) || 0) + r.v); }
    const zSeries = zones.map((z, i) => ({ name: z.n, color: COLS[i % 5], dots: true, values: zYears.map((a) => (zTot.get(a) ? ((zTs.get(a + '|' + z.k) || 0) / (zTot.get(a) as number)) * 100 : null)) }));

    // États / ports d'entrée (filtrés par l'origine choisie)
    const rpv = new Map(B.regions.prev.map((r) => [r.r, r.v]));
    const rTot = B.regions.cur.reduce((s, r) => s + r.v, 0);
    const regs = B.regions.cur.filter((r) => r.v > 0).sort((a, b) => b.v - a.v).map((r) => ({ r: r.r, n: regionName(P, r.r), v: r.v, l: r.l, sh: rTot ? (r.v / rTot) * 100 : 0, d: prevY ? chg(r.v, rpv.get(r.r)) : null }));
    const top5 = regs.slice(0, 5).map((r) => r.r);
    const regEvo = top5.map((rr, i) => ({ name: regionName(P, rr), color: COLS[i], dots: true, values: completeYears.map((a) => { const x = B.regionsTs.find((q) => q.ano === a && q.r === rr); return x ? x.v / 1e6 : null; }) }));
    const regOrig = regs.slice(0, 6).map((rg) => {
      const xs = B.regionOrigins.filter((q) => q.r === rg.r).sort((a, b) => b.v - a.v);
      const tot = xs.reduce((s, q) => s + q.v, 0);
      return { n: rg.n, total: tot, m: xs.slice(0, 3).map((q) => ({ n: cname(q.iso3), sh: tot ? (q.v / tot) * 100 : 0 })) };
    }).filter((r) => r.m.length);
    // Ports et postes-frontières (Brésil) : voie dominante = celle qui porte le plus de valeur
    const portMap = new Map<string, { v: number; l: number; via: Map<string, number> }>();
    for (const r of B.ports || []) { const a = portMap.get(r.urf) || { v: 0, l: 0, via: new Map() }; a.v += r.v; a.l += r.l; a.via.set(r.via, (a.via.get(r.via) || 0) + r.v); portMap.set(r.urf, a); }
    const pTot = [...portMap.values()].reduce((s, a) => s + a.v, 0);
    const ports = [...portMap.entries()].map(([u, a]) => ({ n: portName(u), via: viaName([...a.via.entries()].sort((x, z) => z[1] - x[1])[0][0]), v: a.v, sh: pTot ? (a.v / pTot) * 100 : 0 })).sort((a, b) => b.v - a.v);
    // Voies de transport (Brésil)
    const vTot = (B.via?.cur || []).reduce((s, r) => s + r.v, 0);
    const vpv = new Map((B.via?.prev || []).map((r) => [r.via, r.v]));
    const vias = (B.via?.cur || []).filter((r) => vTot && r.v / vTot >= 0.0005).sort((a, b) => b.v - a.v).map((r) => {
      const os = (B.via?.origins || []).filter((q) => q.via === r.via).sort((a, b) => b.v - a.v).slice(0, 3).map((q) => cname(q.iso3));
      return { k: r.via, n: viaName(r.via), v: r.v, sh: vTot ? (r.v / vTot) * 100 : 0, d: prevY ? chg(r.v, vpv.get(r.via)) : null, fl: r.l ? r.flete / r.l : null, cifGap: r.v ? ((r.cif - r.v) / r.v) * 100 : null, os };
    });

    // Effervescents (tout le marché)
    const spv = new Map<string, number>();
    for (const r of D.B.spark.prev) spv.set(r.iso3, (spv.get(r.iso3) || 0) + r.v);
    const sAgg = new Map<string, { v: number; l: number }>();
    let champ = 0;
    for (const r of D.B.spark.cur) { const a = sAgg.get(r.iso3) || { v: 0, l: 0 }; a.v += r.v; a.l += r.l; sAgg.set(r.iso3, a); if (r.code === '22041010') champ += r.v; }
    const spTot = [...sAgg.values()].reduce((s, a) => s + a.v, 0);
    const spark = [...sAgg.entries()].filter(([, a]) => a.v > 0).map(([iso3, a]) => ({ iso3, n: cname(iso3), v: a.v, l: a.l, sh: spTot ? (a.v / spTot) * 100 : 0, d: prevY ? chg(a.v, spv.get(iso3)) : null, price: a.l ? a.v / a.l : null })).sort((a, b) => b.v - a.v);
    const sparkMine = origin ? spark.findIndex((r) => r.iso3 === origin) : -1;

    // Monde (Comtrade)
    const W0 = D.W;
    const importers = (W0?.importers || []).map((r) => ({ ...r, n: cname(r.iso3) }));
    const impRows = importers.length > 12 && W0?.me && W0.me.rank > 10 ? [...importers.slice(0, 10), null, importers.find((r) => r.iso3 === meta!.pays) || null] : importers.slice(0, 12);
    const sup = (W0?.suppliers || []).filter((s) => s.exports && s.share_pct != null).map((s) => ({ ...s, n: cname(s.iso3) }));
    const supMine = origin ? (W0?.suppliers || []).find((s) => s.iso3 === origin) || null : null;
    const supRows = origin && supMine && !sup.slice(0, 10).some((s) => s.iso3 === origin) && supMine.exports ? [...sup.slice(0, 10), { ...supMine, n: cname(supMine.iso3) }] : sup.slice(0, 10);
    return { y, mm, partial, prevY, cur, prev, p, pp, yearsTbl, completeYears, sY, others, sCur, sAvg, sPart, hiM, loM,
      cats, cTot, organic, evoYears, evoBig, evoSmall, rows, mine, movers, zones, zYears, zSeries, oTot,
      regs, rTot, regEvo, regOrig, ports, vias, spark, spTot, champ, sparkMine, importers, impRows, sup, supMine, supRows };
  });

  // Phrases « L'essentiel »
  const ESS = $derived.by(() => {
    if (!M || !D) return [] as string[];
    const out: string[] = [];
    const partTxt = M.partial ? '(' + tf('k.partial', { y: '', m: monthName(M.mm, true) }).replace(/^,\s*/, '') + ')' : '';
    const dv = M.prev ? chg(M.cur.v, M.prev.v) : null, dl = M.prev ? chg(M.cur.l, M.prev.l) : null, dp = chg(M.p, M.pp);
    if (origin) {
      out.push(X.ess.totalOrig(originName, M.y, M.cur.v, dv, M.cur.l, dl, M.p, dp, partTxt));
      if (M.mine) out.push(X.ess.origRank(M.mine.rank, M.rows.length, M.mine.sh));
    } else {
      out.push(X.ess.total(M.y, M.cur.v, dv, M.cur.l, dl, M.p, dp, partTxt));
      if (M.rows.length > 1) out.push(X.ess.topOrigins({ n: M.rows[0].n, v: M.rows[0].v, sh: M.rows[0].sh, y: M.rows[0].y }, { n: M.rows[1].n, v: M.rows[1].v }));
    }
    if (M.cats.length > 1) out.push(X.ess.cats(M.cats.filter((c) => c.sh >= 0.5).slice(0, 3).map((c) => ({ n: c.short, sh: c.sh }))));
    if (M.regs.length > 2) out.push(X.ess.regions(M.regs.slice(0, 3), M.regs.slice(0, 3).reduce((s, r) => s + r.sh, 0), M.y, !!origin));
    if (origin && M.supMine?.share_pct != null) out.push(X.ess.weight(originName, M.supMine.share_pct, D.wYear));
    if (partialInfo && partialInfo.ano !== M.y) {
      const c = sumY(D.B.monthly, partialInfo.ano, partialInfo.max_mes), q = sumY(D.B.monthly, partialInfo.ano - 1, partialInfo.max_mes);
      out.push(X.ess.partial(partialInfo.ano, partialInfo.max_mes, c.v, chg(c.v, q.v)));
    }
    return out;
  });

  const origRows = $derived.by(() => {
    if (!M) return [];
    if (showAllOrig) return M.rows;
    const top = M.rows.slice(0, 15);
    if (M.mine && M.mine.rank > 15) return [...top, null, M.mine];
    return top;
  });
  // Graduations : une décimale seulement quand le pas n'est pas entier (« 2,5 »), jamais « 0,0 ».
  const ymFmt = (v: number) => nf(v, Number.isInteger(Math.round(v * 1e6) / 1e6) ? 0 : 1);
  const pctFmt = (v: number) => nf(v, 0) + ' %';
  // Avec une origine choisie, les parties qui restent « toutes origines » le disent dans leur ligne de source.
  const allOrig = $derived(origin ? ' · ' + t('meta.allOrig') : '');
  const musdFmt = (v: number) => ymFmt(v) + ' M$';
  const monthLabels = $derived(D ? D.B.monthly.map((r) => r.ano * 100 + r.mes) : []);
</script>

<div class="shell wide">
  <header class="pagehead">
    <span class="pbadge"><i class="ti ti-flag" style="font-size:10px"></i>{t('badge')}</span>
    <h1 class="ptitle">{t('title')}{#if origin}{lang() === 'fr' ? ' : ' : ': '}<span class="a">{originName}</span>{/if}</h1>
    <div class="psub">{t('sub')}</div>
  </header>

  {#if booting}
    <div class="state"><i class="ti ti-loader-2 spin"></i>{t('state.loading')}</div>
  {:else if !meta}
    <div class="state err"><i class="ti ti-alert-triangle"></i>{t('state.error')}
      <button class="btna" onclick={boot} style="margin-left:8px"><i class="ti ti-refresh"></i>{t('state.retry')}</button></div>
  {:else}
    <div class="controls">
      <div class="fctl"><span class="ctrl-lbl">{t('ctl.year')}</span>
        <select class="yearsel" bind:value={year} aria-label={t('ctl.year')}>
          {#each [...meta.years].reverse() as y (y.ano)}<option value={y.ano}>{yearLabel(y.ano)}</option>{/each}
        </select></div>
      <div class="fctl"><span class="ctrl-lbl">{t('ctl.market')}</span>
        <MarketPicker options={mktOpts} value={origin} onChange={(x) => (origin = x)} /></div>
      {#if lastInfo}<span class="fperiod">{tf('ctl.period', { y0: meta.min_year, m: monthName(lastInfo.max_mes, true), y: lastInfo.ano })}</span>{/if}
      {#if busy}<i class="ti ti-loader-2 spin" style="color:var(--tm);font-size:16px"></i>{/if}
    </div>

    {#if err}
      <div class="state err"><i class="ti ti-alert-triangle"></i>{t('state.error')}
        <button class="btna" onclick={() => load(year, origin)} style="margin-left:8px"><i class="ti ti-refresh"></i>{t('state.retry')}</button></div>
    {:else if M && D}
      {@const pv = M.prevY}
      <div class="report">
        <aside class="report-toc"><Toc items={toc} active={activeSec} onGo={goSection} /></aside>
        <div class="report-body">

          <!-- 1. Résumé exécutif -->
          <section class="sec part" id="resumen" data-sec="resumen">
            <SecTitle level={1} num={n('resumen')} title={t('toc.resumen')} id="resumen" />
            <div class="meta-line">{yearLabel(M.y)} · {src} · UN Comtrade</div>
            <div class="ftiles">
              <div class="kpi"><div class="kpi-l">{tf('k.value', { y: yearLabel(M.y) })}</div><div class="kpi-v">{usd(M.cur.v)}</div>
                {#if M.prev}<div class="kpi-sub"><span class="mu-delta {cls(chg(M.cur.v, M.prev.v))}">{sg(chg(M.cur.v, M.prev.v))}</span><span class="kpi-h inl">{tf(M.partial ? 'k.vsSame' : 'k.vs', { y: pv })}</span></div>{/if}</div>
              <div class="kpi"><div class="kpi-l">{tf('k.volume', { y: yearLabel(M.y) })}</div><div class="kpi-v">{mhl(M.cur.l)}</div>
                {#if M.prev}<div class="kpi-sub"><span class="mu-delta {cls(chg(M.cur.l, M.prev.l))}">{sg(chg(M.cur.l, M.prev.l))}</span><span class="kpi-h inl">{tf(M.partial ? 'k.vsSame' : 'k.vs', { y: pv })}</span></div>{/if}</div>
              <div class="kpi"><div class="kpi-l">{tf('k.price', { y: yearLabel(M.y) })}</div><div class="kpi-v">{usdL(M.p)}</div>
                {#if M.pp}<div class="kpi-sub"><span class="mu-delta {cls(chg(M.p, M.pp))}">{sg(chg(M.p, M.pp))}</span><span class="kpi-h inl">{tf(M.partial ? 'k.vsSame' : 'k.vs', { y: pv })}</span></div>{/if}</div>
              {#if origin}
                {#if M.mine}<div class="kpi"><div class="kpi-l">{t('k.origRank')}</div><div class="kpi-v">{M.mine.rank === 1 ? t('rank.first') : tf('rank.nth', { n: M.mine.rank })} / {M.rows.length}</div><div class="kpi-h">{tf('k.origRank.sub', { p: pct(M.mine.sh) })}</div></div>{/if}
              {:else if D.W?.me}
                <div class="kpi"><div class="kpi-l">{t('k.rank')}</div><div class="kpi-v">{D.W.me.rank === 1 ? t('rank.first') : tf('rank.nth', { n: D.W.me.rank })}</div><div class="kpi-h">{tf('k.rank.sub', { p: pct(D.W.me.share_pct), y: D.wYear })}</div></div>
              {/if}
              {#if partialInfo && partialInfo.ano !== M.y}
                {@const c = sumY(D.B.monthly, partialInfo.ano, partialInfo.max_mes)}
                {@const q = sumY(D.B.monthly, partialInfo.ano - 1, partialInfo.max_mes)}
                <div class="kpi"><div class="kpi-l">{tf('k.partial', { y: partialInfo.ano, m: monthName(partialInfo.max_mes, true) })}</div><div class="kpi-v">{usd(c.v)}</div>
                  <div class="kpi-sub"><span class="mu-delta {cls(chg(c.v, q.v))}">{sg(chg(c.v, q.v))}</span><span class="kpi-h inl">{tf('k.vsSame', { y: partialInfo.ano - 1 })}</span></div></div>
              {/if}
            </div>
            <h3 class="sec-h3" style="margin-top:16px">{t('ess.title')}</h3>
            <ul class="takeaways">{#each ESS as s, i (i)}<li>{@html s}</li>{/each}</ul>
          </section>

          <!-- 2. Évolution -->
          <section class="sec part" id="evolucion" data-sec="evolucion"><SecTitle level={1} num={n('evolucion')} title={t('toc.evolucion')} id="evolucion" /></section>
          <section class="sec" id="valor-volumen-precio" data-sec="valor-volumen-precio">
            <SecTitle num={n('valor-volumen-precio')} title={t('toc.vvp')} id="valor-volumen-precio" />
            {#if M.completeYears.length > 1}
              {@const a = M.yearsTbl.find((r) => r.ano === M.completeYears[0])!}
              {@const z = M.yearsTbl.find((r) => r.ano === M.completeYears[M.completeYears.length - 1])!}
              {#if a.v && a.l && z.l}<p class="answer">{@html X.ansVvp(a.ano, z.ano, a.v, z.v, a.l, z.l)}</p>{/if}
            {/if}
            <div class="meta-line">{lastInfo ? tf('meta.monthly', { y0: meta.min_year, m: monthName(lastInfo.max_mes, true), y: lastInfo.ano, s: src }) : src}</div>
            <div class="fcard"><div class="fcard-t">{t('c.monthly')}</div><div class="fcard-s">{t('c.monthly.sub')}</div>
              <LineChart labels={monthLabels} series={[{ name: t('col.value'), color: '#3C4E5A', values: D.B.monthly.map((r) => r.v / 1e6) }]} yFmt={ymFmt}
                xTick={(l) => Number(l) % 100 === 1} xLabel={(l) => String(Math.floor(Number(l) / 100))}
                tipLabel={(l) => monthName(Number(l) % 100, true) + ' ' + Math.floor(Number(l) / 100)} /></div>
            <div class="fcard"><table class="ftbl">
              <thead><tr><th>{t('col.year')}</th><th class="r">{t('col.value')}</th><th class="r">{t('col.var')}</th><th class="r">{t('col.volume')}</th><th class="r">{t('col.price')}</th></tr></thead>
              <tbody>{#each [...M.yearsTbl].reverse() as r (r.ano)}
                <tr class:hl={r.ano === M.y}><td>{r.part ? yearLabel(r.ano) : r.ano}</td><td class="r">{usd(r.v)}</td><td class="r"><span class="t-{cls(r.d)}">{sg(r.d)}</span>{r.part ? '*' : ''}</td><td class="r">{mhl(r.l)}</td><td class="r">{r.l ? usdL(r.v / r.l) : '—'}</td></tr>
              {/each}</tbody></table>
              {#if partialInfo}<div class="fnote">{tf('note.partialVar', { y: partialInfo.ano, p: partialInfo.ano - 1 })}</div>{/if}</div>
          </section>
          <section class="sec" id="mes-a-mes" data-sec="mes-a-mes">
            <SecTitle num={n('mes-a-mes')} title={t('toc.mes')} id="mes-a-mes" />
            {#if M.hiM > 0 && M.loM > 0}<p class="answer">{@html X.ansSeason(M.sY, M.hiM, M.sCur[M.hiM - 1] || 0, M.loM, M.sCur[M.loM - 1] || 0)}</p>{/if}
            <div class="meta-line">{tf('meta.season', { a: String(M.sY) + (M.sPart && partialInfo ? ', ' + partialInfo.ano : ''), s: src })}</div>
            <div class="fcard"><LineChart h={230} labels={Array.from({ length: 12 }, (_, i) => i + 1)} xLabel={(l) => monthName(Number(l), true)} yFmt={musdFmt}
              series={[
                ...(M.others.length ? [{ name: tf('c.avg', { a: M.others[0], b: M.others[M.others.length - 1] }), color: '#8FA0AB', dash: true, values: M.sAvg.map((v) => (v == null ? null : v / 1e6)) }] : []),
                { name: String(M.sY), color: '#3C4E5A', dots: true, values: M.sCur.map((v) => (v == null ? null : v / 1e6)) },
                ...(M.sPart && partialInfo ? [{ name: String(partialInfo.ano), color: '#B95955', dots: true, values: M.sPart.map((v) => (v == null ? null : v / 1e6)) }] : []),
              ]} /></div>
          </section>

          <!-- 3. Vins -->
          <section class="sec part" id="vinos" data-sec="vinos"><SecTitle level={1} num={n('vinos')} title={t('toc.vinos')} id="vinos" /></section>
          <section class="sec" id="tipos" data-sec="tipos">
            <SecTitle num={n('tipos')} title={t('toc.tipos')} id="tipos" />
            {#if M.cats.length}<p class="answer">{@html X.ansCats(M.cats.filter((c, i) => i === 0 || c.sh >= 0.5).map((c) => ({ n: c.n, sh: c.sh, shL: c.shL })), M.organic)}</p>{/if}
            <div class="meta-line">{tf('meta.cats', { y: yearLabel(M.y), s: src })}</div>
            <div class="fcard"><table class="ftbl">
              <thead><tr><th>{t('col.cat')}</th><th class="r">{t('col.value')}</th><th class="r">{t('col.share')}</th>{#if pv}<th class="r">{tf('col.vs', { y: pv })}</th>{/if}<th class="r">{t('col.volume')}</th><th class="r">{t('col.price')}</th></tr></thead>
              <tbody>{#each M.cats as r (r.key)}
                <tr><td>{r.n}</td><td class="r">{usd(r.v)}</td><td class="r">{pct(r.sh)}</td>{#if pv}<td class="r"><span class="t-{cls(r.d)}">{sg(r.d)}</span></td>{/if}<td class="r">{mhl(r.l)}</td><td class="r">{r.l ? usdL(r.v / r.l) : '—'}</td></tr>
              {/each}</tbody></table>
              {#if P === 'bra'}<div class="fnote">{t('note.champ')}</div>{/if}</div>
          </section>
          <section class="sec" id="espumosos" data-sec="espumosos">
            <SecTitle num={n('espumosos')} title={t('toc.spark')} id="espumosos" />
            {#if M.spark.length}
              <p class="answer">{@html X.ansSpark(M.spTot, M.oTot ? (M.spTot / M.oTot) * 100 : 0, M.spark[0] ? { n: M.spark[0].n, v: M.spark[0].v, sh: M.spark[0].sh } : null, M.spark[1] ? { n: M.spark[1].n, v: M.spark[1].v, sh: M.spark[1].sh } : null, P === 'bra' && M.spTot ? (M.champ / M.spTot) * 100 : null)}</p>
              <div class="meta-line">{tf('meta.spark', { y: yearLabel(M.y), s: src })}{allOrig}</div>
              <div class="fcard"><table class="ftbl">
                <thead><tr><th></th><th>{t('col.origin')}</th><th class="r">{t('col.value')}</th><th class="r">{t('col.share')}</th>{#if pv}<th class="r">{tf('col.vs', { y: pv })}</th>{/if}<th class="r">{t('col.volume')}</th><th class="r">{t('col.price')}</th></tr></thead>
                <tbody>{#each (M.sparkMine > 9 ? [...M.spark.slice(0, 10), M.spark[M.sparkMine]] : M.spark.slice(0, 10)) as r, i (r.iso3)}
                  <tr class:hl={r.iso3 === origin} class:ar={r.iso3 === 'ARG' && r.iso3 !== origin}><td class="rk">{i < 10 ? i + 1 : M.sparkMine + 1}</td><td>{r.n}</td><td class="r">{usd(r.v)}</td><td class="r">{pct(r.sh)}</td>{#if pv}<td class="r"><span class="t-{cls(r.d)}">{sg(r.d)}</span></td>{/if}<td class="r">{mhl(r.l)}</td><td class="r">{usdL(r.price)}</td></tr>
                {/each}</tbody></table></div>
            {/if}
          </section>
          <section class="sec" id="tipos-evolucion" data-sec="tipos-evolucion">
            <SecTitle num={n('tipos-evolucion')} title={t('toc.tiposEvo')} id="tipos-evolucion" />
            {#if M.evoYears.length > 1 && M.evoBig.length}
              {@const ib = M.evoYears.length - 1}
              <p class="answer">{@html X.ansEvo(M.evoYears[0], M.evoYears[ib], [...M.evoBig, ...M.evoSmall].filter((s) => Math.max(...s.values) >= 0.02 * Math.max(...M.evoBig[0].values)).slice(0, 4).map((s) => ({ n: s.name, c: chg(s.values[ib], s.values[0]) })))}</p>
              <div class="meta-line">{tf('meta.annual', { a: M.evoYears[0], b: M.evoYears[ib], s: src })}</div>
              {#if M.evoSmall.length}
                <!-- Deux échelles : les petites catégories resteraient collées à zéro sous les grandes. -->
                <div class="fcard"><div class="fcard-t">{M.evoBig.length === 1 ? t(M.evoBig[0].key) : t('c.big')}</div><LineChart h={210} labels={M.evoYears} series={M.evoBig} yFmt={musdFmt} /></div>
                <div class="fcard"><div class="fcard-t">{t('c.small')}</div><LineChart h={210} labels={M.evoYears} series={M.evoSmall} yFmt={musdFmt} /></div>
              {:else}
                <div class="fcard"><LineChart h={250} labels={M.evoYears} series={M.evoBig} yFmt={musdFmt} /></div>
              {/if}
            {/if}
          </section>

          <!-- 4. Pays d'origine -->
          <section class="sec part" id="origenes" data-sec="origenes"><SecTitle level={1} num={n('origenes')} title={t('toc.origenes')} id="origenes" /></section>
          <section class="sec" id="primeros-origenes" data-sec="primeros-origenes">
            <SecTitle num={n('primeros-origenes')} title={t(origin ? 'toc.origPlace' : 'toc.origTop')} id="primeros-origenes" />
            {#if origin && M.mine}<p class="answer">{@html X.ansOrigPlace(originName, M.mine.rank, M.rows.length, M.mine.sh, M.mine.y)}</p>
            {:else if !origin}<p class="answer">{@html X.ansOrigTop(M.rows.length, M.rows.slice(0, 5).reduce((s, r) => s + r.sh, 0))}</p>{/if}
            <div class="meta-line">{yearLabel(M.y)} · {src}</div>
            <div class="fcard"><table class="ftbl">
              <thead><tr><th></th><th>{t('col.origin')}</th><th class="r">{t('col.value')}</th><th class="r">{t('col.share')}</th>{#if pv}<th class="r">{tf('col.vs', { y: pv })}</th>{/if}<th class="r">{t('col.volume')}</th><th class="r">{t('col.price')}</th></tr></thead>
              <tbody>{#each origRows as r, i (r ? r.iso3 : 'gap' + i)}
                {#if r}
                  <tr class:hl={r.iso3 === origin} class:ar={r.iso3 === 'ARG' && r.iso3 !== origin}><td class="rk">{r.rank}</td><td><button class="lnk" type="button" onclick={() => (origin = r.iso3)}>{r.n}</button></td><td class="r">{usd(r.v)}</td><td class="r">{pct(r.sh)}</td>{#if pv}<td class="r"><span class="t-{cls(r.y)}">{sg(r.y)}</span></td>{/if}<td class="r">{mhl(r.l)}</td><td class="r">{usdL(r.price)}</td></tr>
                {:else}
                  <tr class="gap"><td colspan="7">…</td></tr>
                {/if}
              {/each}</tbody></table>
              {#if M.rows.length > 15}<button class="more" type="button" onclick={() => (showAllOrig = !showAllOrig)}>{showAllOrig ? t('less') : tf('more.origins', { n: M.rows.length })}</button>{/if}</div>
          </section>
          <section class="sec" id="lo-que-se-mueve" data-sec="lo-que-se-mueve">
            <SecTitle num={n('lo-que-se-mueve')} title={t('toc.mueve')} id="lo-que-se-mueve" />
            {#if pv && M.movers.dn.length && M.movers.up.length}
              <p class="answer">{@html X.ansMovers(M.movers.dn.map((r) => ({ n: r.n, v: r.v, dv: r.dv })), M.movers.up.map((r) => ({ n: r.n, v: r.v, dv: r.dv })))}</p>
              <div class="meta-line">{tf('meta.movers', { y: yearLabel(M.y), p: pv, s: src })}{allOrig}</div>
              <div class="fgrid2">
                {#each [{ k: 'mv.up', xs: M.movers.up }, { k: 'mv.down', xs: M.movers.dn }] as g (g.k)}
                  <div class="fcard"><div class="fcard-t">{t(g.k)}</div><table class="ftbl">
                    <thead><tr><th>{t('col.origin')}</th><th class="r">{t('col.gap')}</th><th class="r">{t('col.var')}</th><th class="r">{yearLabel(M.y)}</th></tr></thead>
                    <tbody>{#each g.xs as r (r.iso3)}<tr class:hl={r.iso3 === origin}><td>{r.n}</td><td class="r"><span class="t-{cls(r.dv)}">{dUsd(r.dv || 0)}</span></td><td class="r"><span class="t-{cls(r.y)}">{sg(r.y)}</span></td><td class="r">{usd(r.v)}</td></tr>{/each}</tbody></table></div>
                {/each}
              </div>
            {/if}
          </section>
          <section class="sec" id="zonas" data-sec="zonas">
            <SecTitle num={n('zonas')} title={t('toc.zonas')} id="zonas" />
            {#if M.zones.length}
              {@const zs = [...M.zones].sort((a, b) => b.v - a.v)}
              {@const s0 = M.zSeries.find((s) => s.name === zs[0].n)}
              <p class="answer">{@html X.ansZones(zs.slice(0, 3), M.zYears.length > 1 ? M.zYears[0] : null, s0 && s0.values[0] != null ? { n: zs[0].n, sh: s0.values[0] as number } : null)}</p>
              <div class="meta-line">{yearLabel(M.y)} · {src}{allOrig}</div>
              <div class="fcard"><table class="ftbl">
                <thead><tr><th>{t('col.zone')}</th><th class="r">{t('col.value')}</th><th class="r">{t('col.share')}</th>{#if pv}<th class="r">{tf('col.vs', { y: pv })}</th>{/if}<th>{t('col.mainOrig')}</th></tr></thead>
                <tbody>{#each zs as z (z.k)}<tr><td>{z.n}</td><td class="r">{usd(z.v)}</td><td class="r">{pct(z.sh)}</td>{#if pv}<td class="r"><span class="t-{cls(z.d)}">{sg(z.d)}</span></td>{/if}<td class="muted">{z.top.join(', ')}</td></tr>{/each}</tbody></table></div>
              {#if M.zYears.length > 1}
                <div class="fcard"><div class="fcard-t">{t('c.zoneShare')}</div><div class="fcard-s">{tf('meta.annual', { a: M.zYears[0], b: M.zYears[M.zYears.length - 1], s: src })}</div>
                  <LineChart h={220} labels={M.zYears} series={M.zSeries} yFmt={pctFmt} /></div>
              {/if}
            {/if}
          </section>

          <!-- 5. États brésiliens / ports d'entrée américains -->
          <section class="sec part" id={RID} data-sec={RID}><SecTitle level={1} num={n(RID)} title={t('toc.reg')} id={RID} /></section>
          <section class="sec" id={'mapa-' + RID} data-sec={'mapa-' + RID}>
            <SecTitle num={n('mapa-' + RID)} title={t('toc.mapa')} id={'mapa-' + RID} />
            {#if M.regs.length > 2}<p class="answer">{@html X.ansMap(M.regs.slice(0, 3), M.regs.slice(0, 3).reduce((s, r) => s + r.sh, 0), !!origin)}</p>{/if}
            <div class="meta-line">{yearLabel(M.y)} · {src}</div>
            <!-- Brésil : carte et tableau côte à côte (comme /francia/) ; États-Unis : carte large au-dessus du tableau. -->
            <div class={P === 'bra' ? 'fmapwrap' : ''}>
              <div class="fcard">{#if P === 'bra'}<BrMap rows={M.regs} year={yearLabel(M.y)} />{:else}<UsMap rows={M.regs} year={yearLabel(M.y)} />{/if}</div>
              <div class="fcard"><table class="ftbl">
                {#if showPorts && P === 'bra'}
                  <thead><tr><th></th><th>{t('col.port')}</th><th>{t('col.via')}</th><th class="r">{t('col.value')}</th><th class="r">{t('col.share')}</th></tr></thead>
                  <tbody>{#each M.ports.slice(0, 15) as d, i (d.n + i)}<tr><td class="rk">{i + 1}</td><td>{d.n}</td><td class="muted">{d.via}</td><td class="r">{usd(d.v)}</td><td class="r">{pct(d.sh)}</td></tr>{/each}</tbody>
                {:else}
                  <thead><tr><th></th><th>{t('col.region')}</th><th class="r">{t('col.value')}</th><th class="r">{t('col.share')}</th>{#if pv}<th class="r">{tf('col.vs', { y: pv })}</th>{/if}</tr></thead>
                  <tbody>{#each M.regs.slice(0, 14) as r, i (r.r)}<tr><td class="rk">{i + 1}</td><td>{r.n}</td><td class="r">{usd(r.v)}</td><td class="r">{pct(r.sh)}</td>{#if pv}<td class="r"><span class="t-{cls(r.d)}">{sg(r.d)}</span></td>{/if}</tr>{/each}</tbody>
                {/if}
              </table>
              {#if P === 'bra'}<button class="more" type="button" onclick={() => (showPorts = !showPorts)}>{showPorts ? t('more.regions') : t('more.ports')}</button>{/if}</div>
            </div>
            <div class="fnote">{t('note.reg')}</div>
          </section>
          <section class="sec" id={RID + '-evolucion'} data-sec={RID + '-evolucion'}>
            <SecTitle num={n(RID + '-evolucion')} title={t('toc.regEvo')} id={RID + '-evolucion'} />
            {#if M.completeYears.length > 1 && M.regEvo.length}
              {@const ib = M.completeYears.length - 1}
              <p class="answer">{@html X.ansEvo(M.completeYears[0], M.completeYears[ib], [...M.regEvo].map((s) => ({ n: s.name, c: chg(s.values[ib], s.values[0]) })).filter((x) => x.c != null).sort((a, b) => (b.c ?? 0) - (a.c ?? 0)))}</p>
              <div class="meta-line">{tf('meta.annual', { a: M.completeYears[0], b: M.completeYears[ib], s: src })}</div>
              <div class="fcard"><LineChart h={250} labels={M.completeYears} series={M.regEvo} yFmt={musdFmt} /></div>
            {/if}
          </section>
          <section class="sec" id={RID + '-origenes'} data-sec={RID + '-origenes'}>
            <SecTitle num={n(RID + '-origenes')} title={t(origin ? 'toc.regOrigSel' : 'toc.regOrig')} id={RID + '-origenes'} />
            {#if origin}
              {#if M.regs.length}<p class="answer">{@html X.ansRegSel(M.regs.map((d) => ({ n: d.n, v: d.v })), M.regs.slice(0, 3).reduce((s, d) => s + d.sh, 0))}</p>{/if}
              <div class="meta-line">{yearLabel(M.y)} · {src}</div>
              <div class="fcard"><table class="ftbl">
                <thead><tr><th></th><th>{t('col.region')}</th><th class="r">{t('col.value')}</th><th class="r">{t('col.share')}</th><th class="r">{t('col.volume')}</th><th class="r">{t('col.price')}</th></tr></thead>
                <tbody>{#each M.regs.slice(0, 10) as d, i (d.r)}<tr><td class="rk">{i + 1}</td><td>{d.n}</td><td class="r">{usd(d.v)}</td><td class="r">{pct(d.sh)}</td><td class="r">{mhl(d.l)}</td><td class="r">{d.l ? usdL(d.v / d.l) : '—'}</td></tr>{/each}</tbody></table></div>
            {:else if M.regOrig.length}
              <p class="answer">{@html X.ansRegOrig(M.regOrig.map((r) => ({ n: r.n, m: r.m[0]?.n || '' })))}</p>
              <div class="meta-line">{tf('meta.regOrig', { y: yearLabel(M.y), s: src })}</div>
              <div class="fcard"><table class="ftbl">
                <thead><tr><th>{t('col.region')}</th><th class="r">{t('col.imports')}</th><th>{t('col.o1')}</th><th>{t('col.o2')}</th><th>{t('col.o3')}</th></tr></thead>
                <tbody>{#each M.regOrig as r (r.n)}<tr><td>{r.n}</td><td class="r">{usd(r.total)}</td>{#each r.m as x (x.n)}<td>{x.n} <span class="muted">{pct(x.sh, 0)}</span></td>{/each}{#each Array(3 - r.m.length) as _, k (k)}<td></td>{/each}</tr>{/each}</tbody></table></div>
            {/if}
          </section>
          {#if P === 'bra'}
            <section class="sec" id="transporte" data-sec="transporte">
              <SecTitle num={n('transporte')} title={t('toc.transporte')} id="transporte" />
              {#if M.vias.length}
                {@const road = M.vias.find((v) => v.k === 'RODOVIARIA')}
                {@const sea = M.vias.find((v) => v.k === 'MARITIMA')}
                <p class="answer">{@html X.ansVia(road?.sh || 0, sea?.sh || 0, road?.fl ?? null, sea?.fl ?? null, origin ? [] : (road?.os || []).slice(0, 2))}</p>
                <div class="meta-line">{tf('meta.via', { y: yearLabel(M.y), s: src })}</div>
                <div class="fcard"><table class="ftbl">
                  <thead><tr><th>{t('col.via')}</th><th class="r">{t('col.value')}</th><th class="r">{t('col.share')}</th>{#if pv}<th class="r">{tf('col.vs', { y: pv })}</th>{/if}<th class="r">{t('col.freight')}</th><th class="r">{t('col.cifGap')}</th>{#if !origin}<th>{t('col.mainOrig')}</th>{/if}</tr></thead>
                  <tbody>{#each M.vias as v (v.k)}<tr><td>{v.n}</td><td class="r">{usd(v.v)}</td><td class="r">{pct(v.sh)}</td>{#if pv}<td class="r"><span class="t-{cls(v.d)}">{sg(v.d)}</span></td>{/if}<td class="r">{usdL(v.fl)}</td><td class="r">{v.cifGap != null ? '+' + pct(v.cifGap) : '—'}</td>{#if !origin}<td class="muted">{v.os.join(', ')}</td>{/if}</tr>{/each}</tbody></table></div>
              {/if}
            </section>
          {/if}

          <!-- 6. Le pays dans le monde -->
          <section class="sec part" id="mundo" data-sec="mundo"><SecTitle level={1} num={n('mundo')} title={t('toc.mundo')} id="mundo" /></section>
          <section class="sec" id="importadores" data-sec="importadores">
            <SecTitle num={n('importadores')} title={t('toc.importadores')} id="importadores" />
            {#if D.W?.me}
              <p class="answer">{@html X.ansWorld(D.W.me.rank, D.W.me.share_pct, D.W.me.rank_volume, D.W.me.price_l, D.W.world.price_l)}</p>
              <div class="meta-line">{tf('meta.comtrade', { y: D.wYear })}{D.wYear !== M.y ? ' · ' + tf('note.worldYear', { y: D.wYear }) : ''}{allOrig}</div>
              <div class="fcard"><table class="ftbl">
                <thead><tr><th></th><th>{t('col.importer')}</th><th class="r">{t('col.imports')}</th><th class="r">{t('col.worldShare')}</th><th class="r">{t('col.price')}</th><th class="r">{t('col.rankVol')}</th></tr></thead>
                <tbody>{#each M.impRows as r, i (r ? r.iso3 : 'gap' + i)}
                  {#if r}
                    <tr class:hl={r.iso3 === meta.pays}><td class="rk">{r.rank}</td><td>{r.n}</td><td class="r">{usd(r.v)}</td><td class="r">{pct(r.share_pct)}</td><td class="r">{usdL(r.price_l)}</td><td class="r">{r.rank_volume === 1 ? t('rank.first') : tf('rank.nth', { n: r.rank_volume })}</td></tr>
                  {:else}
                    <tr class="gap"><td colspan="6">…</td></tr>
                  {/if}
                {/each}</tbody></table></div>
            {/if}
          </section>
          <section class="sec" id="peso-proveedores" data-sec="peso-proveedores">
            <SecTitle num={n('peso-proveedores')} title={t('toc.peso')} id="peso-proveedores" />
            {#if M.sup.length > 1}
              <p class="answer">{@html origin ? X.ansWeightSel(originName, M.supMine?.share_pct ?? null) : X.ansWeight(M.sup.slice(0, 3).map((s) => ({ n: s.n, v: s.to_c, sh: s.share_pct as number })))}</p>
              <div class="meta-line">{tf('meta.weight', { y: D.wYear, s: src })}</div>
              <div class="fcard"><table class="ftbl">
                <thead><tr><th>{t('col.supplier')}</th><th class="r">{t('col.exportsTot')}</th><th class="r">{t('col.toC')}</th><th class="r">{t('col.weight')}</th></tr></thead>
                <tbody>{#each M.supRows as r (r.iso3)}
                  <tr class:hl={r.iso3 === origin} class:ar={r.iso3 === 'ARG' && r.iso3 !== origin}><td>{r.n}</td><td class="r">{usd(r.exports)}</td><td class="r">{usd(r.to_c)}</td><td class="r">{pct(r.share_pct)}</td></tr>{/each}</tbody></table></div>
            {/if}
          </section>

          <!-- 7. Méthode -->
          <section class="sec part" id="metodologia" data-sec="metodologia">
            <SecTitle level={1} num={n('metodologia')} title={t('toc.metodo')} id="metodologia" />
            <div class="fcard"><ul class="fmethod">{#each ['m.1', 'm.2', 'm.3', 'm.4', 'm.5', 'm.6'] as k (k)}<li>{@html t(k)}</li>{/each}</ul></div>
          </section>
        </div>
      </div>
    {:else}
      <div class="state"><i class="ti ti-loader-2 spin"></i>{t('state.loading')}</div>
    {/if}
  {/if}
</div>
